import Feature from '@/interfaces/geo/feature.interface';
import {Observable, of, Subject} from 'rxjs';
import {delay, map, mergeMap, take} from 'rxjs/operators';
import MapFeaturesService from '@/services/map-features.service';
import MammadabaMap from '@/interfaces/geo/mammadaba-map.interface';
import LayerVisibility from '@/interfaces/geo/layer-visibility.interface';
import {CustomObservable} from '@/library/observable/custom-observable';
import Filters from '@/library/filter/filters';

declare const GeoMammadabaMap: MammadabaMap;

export default class MapService {
    public whenMapObjectSelected: Subject<Feature> = new Subject();
    public whenMapObjectUnselected: Subject<Feature> = new Subject();
    public whenRouteSelected: Subject<Feature> = new Subject();
    public whenRouteUnselected: Subject<Feature> = new Subject();
    private whenMapMoveEnd: Subject<void> = new Subject();
    private static instance: MapService;
    private mapFeaturesService: MapFeaturesService = MapFeaturesService.getInstance();
    private mapMoveInAction: boolean = false;
    private lastRenderedFeatures: { objects: Feature[], routes: Feature[] } = {objects: [], routes: []};
    private lastRenderedLayerVisibilities: LayerVisibility[] = [];
    private lastZoomedFeature: Feature | null = null;
    private lastSelectedRoute: Feature | null = null;
    private lastFilters: Filters | null = null;
    private isNearbyCircleVisible: boolean = false;
    private localPadding: { left: number, duration: number } = {left: 0, duration: 850};

    private constructor() {
        this.subscribeOnEvents();
    }

    public static getInstance(): MapService {
        if (!MapService.instance) {
            MapService.instance = new MapService();
        }
        return MapService.instance;
    }

    public zoomToFeature(feature: Feature): Observable<void> {
        this.lastZoomedFeature = feature;
        return this.renderMap().pipe(map(() => {
            setTimeout(() => this.lastZoomedFeature = null, 1000);
        }));
    }

    public markFeatureSelected(feature: Feature): void {
        this.mapFeaturesService.updateAttributes(feature, {selectedState: 1});
    }

    public markFeatureUnselected(feature: Feature): void {
        this.mapFeaturesService.updateAttributes(feature, {selectedState: 0});
    }

    public toggleNearbyCircle(visible: boolean): void {
        this.isNearbyCircleVisible = visible;
    }

    public selectRoute(feature: Feature): Observable<void> {
        this.lastSelectedRoute = feature;
        return this.renderMap();
    }

    public unselectRoute(): Observable<void> {
        this.lastSelectedRoute = null;
        return this.renderMap();
    }

    public changePadding(paddingLeft: number): Observable<void> {
        this.localPadding.left = paddingLeft;
        return this.renderMap();
    }

    public renderFeatures(objects: Feature[], routes: Feature[], filters: Filters): Observable<void> {
        this.lastRenderedFeatures.objects = objects;
        this.lastRenderedFeatures.routes = routes;
        this.lastFilters = filters;
        return this.renderMap();
    }

    public changeLayerVisibilities(layerVisibilities: LayerVisibility[]): Observable<void> {
        this.lastRenderedLayerVisibilities = layerVisibilities;
        return this.renderMap();
    }

    private renderMap(): Observable<void> {
        return of(null).pipe(
            mergeMap(() => {
                const allFeatures: Feature[] = this.mapFeaturesService.allFeatures();
                const allRouteLayers: Feature[][] = this.mapFeaturesService.allRouteLayers();
                // Map always show all objects on re-render if we don`t filter them from allObjects
                this.lastRenderedFeatures.objects = allFeatures.filter(feature => {
                    return this.lastRenderedFeatures.objects.some(checkFeature => checkFeature.getId() === feature.getId());
                });
                GeoMammadabaMap.renderMap(
                    {
                        features: this.lastRenderedFeatures.objects,
                        layerVisibility: this.lastRenderedLayerVisibilities,
                        allFeatures: allFeatures,
                        selectFeatureById: null,
                        zoomTo: this.lastZoomedFeature,
                        mapVisible: true,
                        padding: this.localPadding,
                        nearbyCircleVisible: this.isNearbyCircleVisible,
                        filterOptions: this.lastFilters ? this.lastFilters.filtersObject() : null,
                        onClickOnFeature: this.handleObjectClick.bind(this),
                        onUnselect: this.handleObjectUnselect.bind(this),
                        // ROUTES
                        routeLayers: allRouteLayers,
                        filteredRoutes: this.lastRenderedFeatures.routes,
                        selectRouteById: this.lastSelectedRoute
                            ? {routeId: this.lastSelectedRoute.values_.ROUTEID, timestamp: Date.now()}
                            : null,
                        onRouteSelect: this.handleRouteSelect.bind(this),
                        onRouteUnselect: this.handleRouteUnselect.bind(this),
                        // GAME
                        gameLayers: [],
                        selectGameById: null,
                        selectGameCheckPointById: null,
                        startGame: null,
                        gameCompleted: null,
                        gameApproachingRadius: 0,
                        gameLeavingRadius: 0,
                        checkPointFound: null,
                        useGameSimulator: false,
                        onGameSelected: () => {},
                        onGameLocationChange: () => {},
                        onGameCompleted: () => {},
                        onCheckPointSelected: () => {},
                    },
                    this.mapElement()
                );
                return this.mapReadyForNextRender();
            })
        );
    }

    private handleObjectClick(feature: Feature): void {
        this.whenMapObjectSelected.next(feature);
    }

    private handleObjectUnselect(objectId: number): void {
        const unselectedFeature = this.mapFeaturesService.allFeatures().find(feature => feature.values_.OBJECTID === objectId);
        if (unselectedFeature) {
            this.whenMapObjectUnselected.next(unselectedFeature);
        }
    }

    private handleRouteSelect(feature: Feature): void {
        this.lastSelectedRoute = feature;
        this.whenRouteSelected.next(feature);
    }

    private handleRouteUnselect(routeId: string): void {
        this.lastSelectedRoute = null;
        const routeLayers = this.mapFeaturesService.allRouteLayers();
        const unselectedRoute = (routeLayers[0] || []).find(feature => feature.values_.ROUTEID === routeId);
        if (unselectedRoute) {
            this.whenRouteUnselected.next(unselectedRoute);
        }
    }

    private subscribeOnEvents(): void {
        document.addEventListener('movestart', (event) => {
            this.mapMoveInAction = true;
        });
        document.addEventListener('moveend', (event) => {
            this.mapMoveInAction = false;
            this.whenMapMoveEnd.next();
        });
    }

    private mapReadyForNextRender(): Observable<void> {
        return CustomObservable.empty().pipe(
            delay(50),
            mergeMap(() => {
                return this.mapMoveInAction
                    ? this.whenMapMoveEnd.pipe(take(1))
                    : CustomObservable.empty();
            }),
        );
    }

    private mapElement(): HTMLDivElement {
        return document.getElementById('map') as HTMLDivElement || new HTMLDivElement();
    }
}
